/**
 * Saving what a leg is for — ROADMAP 9.10.
 *
 * Optimistic, unlike `useLegTransfer`, and the difference is the point: a
 * role is DESCRIPTIVE. The server cannot refuse it on a read the browser
 * never saw — there is no state in which labelling a leg is unsafe — so the
 * chip can change under the cursor and be corrected only if the write
 * actually fails. A move had to wait for the server because a move can be
 * refused; this cannot.
 */

import { useCallback, useState } from 'react'
import type { LegRole } from '@apna/shared'
import { api } from '@/lib/api'

export function useLegRole(onSaved: (tradeLegId: string, role: LegRole | null) => void): {
  /** The leg whose write is in flight, or null. */
  saving: string | null
  /** Why the last write failed, or null. */
  error: string | null
  setRole: (tradeLegId: string, role: LegRole | null) => Promise<void>
} {
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const setRole = useCallback(
    async (tradeLegId: string, role: LegRole | null): Promise<void> => {
      setSaving(tradeLegId)
      setError(null)
      // Shown immediately; reverted by the caller's own re-read if this fails.
      onSaved(tradeLegId, role)
      try {
        await api.patch(`/trades/legs/${tradeLegId}/role`, { role })
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'The label could not be saved.')
      } finally {
        setSaving(null)
      }
    },
    [onSaved],
  )

  return { saving, error, setRole }
}
